/**
 * Deterministic counting for the work the table performs.
 *
 * Wall-clock timings are too noisy to assert on in CI, but the number of times the table renders a
 * component or recomputes a selector in response to a given interaction is deterministic, so it can
 * be asserted exactly. The work-count suite in `core-ui/__tests__/perf/` turns counting on, drives a
 * scenario, and compares the counts against committed baselines.
 *
 * Renders alone do not explain why a render happened, and some of the most expensive work the table
 * does is invisible to them. Each counter family below exists so that a change meant to remove one
 * of those costs has a metric that proves it.
 *
 * Counting is off unless a test turns it on, so each instrumented site pays for one comparison
 * against a module-level null.
 */

/**
 * The components whose renders are counted. Anything that renders once per cell or per row belongs
 * here; the whole point is to notice when a single-cell interaction re-renders the whole table.
 */
export const INSTRUMENTED_TABLE_COMPONENTS = [
  'TableEditableClass',
  'TableEditableComponent',
  'TableRow',
  'TableCell',
  'TableAxisCell',
  'TableHeaderRow',
  'TableCellEditor',
] as const;

export type InstrumentedTableComponent = (typeof INSTRUMENTED_TABLE_COMPONENTS)[number];

export type TableRenderCounts = Record<InstrumentedTableComponent, number>;

/**
 * What a selector cost. Calls are deliberately not counted: a memoized selector called again with
 * the same store returns from reselect's argument memoization without running its inputs, so a call
 * count measures how many places read a value rather than how much work happened. It moves with
 * almost any refactor while the work stays the same, which would make these baselines churn for
 * reasons nobody should have to read a diff about.
 */
export interface SelectorCounts {
  /** How many times the selector's combiner ran because its inputs had changed. */
  recomputations: number;
  /** How many times the selector compared a fresh result against the previous one. */
  resultEqualityChecks: number;
}

/**
 * Everything counted during one measurement phase. The shape grows one family at a time as each
 * metric lands; every family is keyed so the work-count diff can report which counter moved.
 */
export interface TableInstrumentationCounts {
  renders: TableRenderCounts;
  /** Keyed by the name each selector is created with, and holding only selectors that ran. */
  selectors: Record<string, SelectorCounts>;
}

interface Counters {
  renders: TableRenderCounts;
  selectors: Record<string, SelectorCounts>;
}

function zeroedCounters(): Counters {
  const renders = {} as TableRenderCounts;
  for (const component of INSTRUMENTED_TABLE_COMPONENTS) {
    renders[component] = 0;
  }
  return {renders, selectors: {}};
}

let counters: Counters | null = null;

/**
 * Whether a scenario is counting right now.
 *
 * The wrapping sites (each named selector) consult this when they are built, so a table built by
 * any other test is left uninstrumented: it cannot be counted, and it cannot add to another
 * scenario's numbers. A scenario therefore has to start counting before it mounts the table it
 * means to measure, which `measureTableWork` does.
 */
export function isTableCountingActive(): boolean {
  return counters != null;
}

/** Begin counting table work, discarding any counts from a previous scenario. */
export function startCountingTableWork(): void {
  counters = zeroedCounters();
}

/** Stop counting table work. Safe to call when counting was never started. */
export function stopCountingTableWork(): void {
  counters = null;
}

/**
 * Zero the counters while leaving counting on, so a scenario can measure an interaction without the
 * mount that preceded it.
 */
export function resetTableCounts(): void {
  if (counters != null) {
    counters = zeroedCounters();
  }
}

export function readTableCounts(): TableInstrumentationCounts {
  if (counters == null) {
    throw new Error('Table counting is not active. Call startCountingTableWork() first.');
  }
  return {
    renders: {...counters.renders},
    selectors: Object.fromEntries(
      Object.entries(counters.selectors).map(([name, selectorCounts]) => [name, {...selectorCounts}])
    ),
  };
}

/**
 * Record one render of `component`. Called from the component's render body, so it runs once per
 * render attempt regardless of whether React commits the result.
 */
export function countTableRender(component: InstrumentedTableComponent): void {
  if (counters != null) {
    counters.renders[component] += 1;
  }
}

function selectorCounts(name: string): SelectorCounts | null {
  if (counters == null) {
    return null;
  }
  const existing = counters.selectors[name];
  if (existing != null) {
    return existing;
  }
  const created = {recomputations: 0, resultEqualityChecks: 0};
  counters.selectors[name] = created;
  return created;
}

/** Record one run of the named selector's combiner. */
export function countSelectorRecomputation(name: string): void {
  const selector = selectorCounts(name);
  if (selector != null) {
    selector.recomputations += 1;
  }
}

/** Record one comparison of the named selector's fresh result against its previous one. */
export function countSelectorResultEqualityCheck(name: string): void {
  const selector = selectorCounts(name);
  if (selector != null) {
    selector.resultEqualityChecks += 1;
  }
}
